'use client';

/**
 * Per-surface "unread" indicator.
 *
 * Flashing emerald dot when a surface's freshestAsOf is newer than the user's
 * last viewed timestamp for that surface (stored in localStorage). Solid (or
 * hidden) once the surface has been viewed at its current data version.
 *
 * Click-to-view tracking lives in PageTabs — when a user clicks a tab, the
 * tab's surface is marked viewed via markViewed(). On mount the active tab is
 * also marked viewed so the tab the user is currently looking at doesn't
 * sit there flashing.
 *
 * Usage:
 *   <UnreadDot surfaceId="overnight" />
 *   markViewed('overnight', freshestAsOfIso)
 */

import { useEffect, useState } from 'react';
import { useResource, fetchWithCache, invalidateResource } from '@/lib/use-resource';

interface SurfaceStatus {
  id: string;
  freshestAsOf: string | null;
  staleDays: number | null;
  status: 'ok' | 'stale' | 'empty' | 'missing';
}

interface SurfacesResponse {
  today: string;
  surfaces: SurfaceStatus[];
}

// Pipeline-status: every dot goes yellow flashing while data is being pulled.
// UnreadDot reads `running` (daily-refresh.log mtime within 5 min) plus
// any-queued, then renders amber instead of emerald during that window.
interface PipelinePollResponse { running: boolean; today: string; runs: Array<{ date: string; pulls: Record<string, { status: 'ok' | 'fail' | 'queued' }> }> }

function pipelineIsRefreshing(d: PipelinePollResponse | null): boolean {
  if (!d) return false;
  const todayRun = d.runs?.find((r) => r.date === d.today);
  const anyQueued = todayRun ? Object.values(todayRun.pulls).some((p) => p.status === 'queued') : false;
  return d.running === true || anyQueued;
}

const STORAGE_PREFIX = 'surface:lastViewed:';
function readLastViewed(surfaceId: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + surfaceId);
  } catch {
    return null;
  }
}
function writeLastViewed(surfaceId: string, asOf: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + surfaceId, asOf);
  } catch {
    /* private mode etc. — silent */
  }
  // Notify other dots in the page so the flashing one for the surface we just
  // viewed flips to solid immediately, without needing a remount.
  window.dispatchEvent(new CustomEvent('surface:viewed', { detail: { surfaceId, asOf } }));
}

/** Public helper used by the tab row when a tab is clicked. */
export function markViewed(surfaceId: string, asOf?: string | null) {
  if (asOf) writeLastViewed(surfaceId, asOf);
}

/** Helper for callers that don't have asOf in hand — fetches and marks. */
export async function markViewedAuto(surfaceId: string) {
  try {
    const d = await fetchWithCache<SurfacesResponse>('/api/data-surfaces');
    const found = d.surfaces.find((x) => x.id === surfaceId);
    if (found?.freshestAsOf) writeLastViewed(surfaceId, found.freshestAsOf);
  } catch {
    /* silent */
  }
}

export function UnreadDot({
  surfaceId,
  className = '',
}: {
  surfaceId: string;
  className?: string;
}) {
  // Both /api/data-surfaces and /api/pipeline-status are shared via useResource —
  // every dot on the page reads from the same cached payloads, no per-dot fetch.
  const { data: surfaces } = useResource<SurfacesResponse>('/api/data-surfaces');
  const { data: pipeline } = useResource<PipelinePollResponse>('/api/pipeline-status');
  const found = surfaces?.surfaces.find((x) => x.id === surfaceId) ?? null;
  const asOf = found?.freshestAsOf ?? null;
  const status = found?.status ?? null;
  const refreshing = pipelineIsRefreshing(pipeline);

  const [lastViewed, setLastViewed] = useState<string | null>(() => readLastViewed(surfaceId));

  // 60-second polling: invalidate then trigger a refetch by reading via
  // useResource's cache cycle. AppNav also polls and shares the cache, so
  // typically AppNav's interval will keep the cache warm — this is belt &
  // suspenders for pages where AppNav isn't mounted (none today, but…).
  useEffect(() => {
    const id = setInterval(() => {
      invalidateResource('/api/pipeline-status');
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // React to other dots in the page marking this surface as viewed.
  useEffect(() => {
    function onViewed(e: Event) {
      const detail = (e as CustomEvent).detail as { surfaceId: string; asOf: string };
      if (detail.surfaceId === surfaceId) setLastViewed(detail.asOf);
    }
    window.addEventListener('surface:viewed', onViewed);
    return () => window.removeEventListener('surface:viewed', onViewed);
  }, [surfaceId]);

  if (!asOf || status === 'missing' || status === 'empty') return null;

  const isUnread = !lastViewed || asOf > lastViewed;
  const isStale = status === 'stale';

  // Steve directive 2026-05-04 — three colors only:
  //   yellow → in process (refresh in flight OR surface is stale waiting
  //            for its next pull)
  //   green  → fresh; flashing when unread, solid when caught up
  //   red    → reserved for missing/empty (caller branch above hides those)
  const dotClass = refreshing || isStale
    ? 'bg-amber-300 animate-pulse shadow-[0_0_8px_rgba(252,211,77,0.9)]'
    : isUnread
      ? 'bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.9)]'
      : 'bg-emerald-400/70';
  const title = refreshing
    ? 'Refresh in progress — data is being pulled'
    : isStale
      ? `Waiting for next refresh (data through ${asOf.slice(0, 10)})`
      : isUnread
        ? `New since you last viewed (${asOf.slice(0, 10)})`
        : `Up to date (${asOf.slice(0, 10)})`;

  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass} ${className}`}
      title={title}
    />
  );
}
